// Shared markdown utilities for Arnold (AI Coach)
// Keep this small and predictable: sanitize only what breaks rendering.

export const sanitizeMarkdown = (text: string): string => {
  let sanitized = text;

  // 0) Fix heading syntax: convert single # to ## (Arnold is instructed to use ##)
  sanitized = sanitized.replace(/^#\s+(?!#)/gm, "## ");
  sanitized = sanitized.replace(/(\n)#\s+(?!#)/g, "$1## ");

  // Unescape headings that were incorrectly escaped (\#)
  sanitized = sanitized.replace(/^\#\s+/gm, "## ");
  sanitized = sanitized.replace(/(\n)\#\s+/g, "$1## ");

  // 1) Close unclosed bold tags (**)
  const boldMatches = sanitized.match(/\*\*/g);
  if (boldMatches && boldMatches.length % 2 !== 0) {
    const lastIndex = sanitized.lastIndexOf("**");
    if (lastIndex !== -1) {
      sanitized = sanitized.substring(0, lastIndex) + sanitized.substring(lastIndex + 2);
    }
  }

  // 2) Close unclosed italic tags (*) - conservative; avoid breaking lists
  const singleAsterisks = (sanitized.match(/(?<!\*)\*(?!\*)/g) || []).length;
  if (singleAsterisks % 2 !== 0) {
    sanitized = sanitized.replace(/(?<!\*)\*(?!\*)(?![*-])(?![^\n]*$)/g, "");
  }

  // 3) Close unclosed underline tags (__)
  const underlineMatches = sanitized.match(/__/g);
  if (underlineMatches && underlineMatches.length % 2 !== 0) {
    const lastIndex = sanitized.lastIndexOf("__");
    if (lastIndex !== -1) {
      sanitized = sanitized.substring(0, lastIndex) + sanitized.substring(lastIndex + 2);
    }
  }

  // 4) Normalize bullets: convert * bullets to - bullets (avoid **bold**)
  sanitized = sanitized.replace(/^\s*\*\s+(?!\*)/gm, "- ");
  sanitized = sanitized.replace(/([^\n])\s+\*\s+(?!\*)/g, "$1 - ");

  // 5) Normalize paragraph spacing
  sanitized = sanitized.replace(/\n{3,}/g, "\n\n");

  return sanitized;
};

/**
 * Converts inline dash patterns to proper list items on separate lines
 * Handles patterns like: "text. - **Item:** description - **Item2:** description"
 */
const convertInlineDashesToList = (text: string): string => {
  // Split inline dash-delimited list items onto separate lines
  // Pattern: ". - **Something" or "! - **Something" or "? - **Something"
  let result = text.replace(/([.!?])\s+-\s+(\*\*)/g, "$1\n\n- $2");
  
  // Also handle cases where there's text before the first dash item
  // "Here's a workout: - **Exercise 1:**" -> "Here's a workout:\n\n- **Exercise 1:**"
  result = result.replace(/:\s+-\s+(\*\*)/g, ":\n\n- $1");
  
  return result;
};

/**
 * Splits headings/titles that are accidentally merged with following sentences
 * Handles: "Your Next Power Session! It sounds like..." -> heading + paragraph
 */
const splitMergedTitleSentences = (text: string): string => {
  const lines = text.split("\n");
  const result: string[] = [];

  // Common sentence starters that indicate a new sentence after a title
  const lowercaseStarters = /^(it|i|sounds|let's|lets|here's|heres|this|that|the|a|an|we|you|your|my|our|based|given|considering|looking|after|before|since|because|as|for|with|from|to|in|on|at|so|now|alright|okay|ok)\b/i;
  
  // Capitalized sentence starters
  const capitalizedStarters = /^(It|I|Sounds|Let's|Lets|Here's|Heres|This|That|The|A|An|We|You|Your|My|Our|Based|Given|Considering|Looking|After|Before|Since|Because|As|For|With|From|To|In|On|At|So|Now|Alright|Okay|Ok|Remember|Great|Excellent|Nice|Solid|Good|Amazing|Keep|Today|Ready|Absolutely|Definitely|Certainly|Sure|Perfect|Awesome|Fantastic|Wonderful)\b/;

  for (const line of lines) {
    // Skip empty lines
    if (!line.trim()) {
      result.push(line);
      continue;
    }

    // Check for heading lines (## Title merged with sentence)
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const [, hashes, content] = headingMatch;
      
      // Look for pattern: "Title! sentence" or "Title? sentence" or "Title. sentence"
      const mergedMatch = content.match(/^(.{3,60}[!?.])\s+([A-Za-z].+)$/);
      if (mergedMatch) {
        const [, title, sentence] = mergedMatch;
        // Only split if the sentence part starts with a common sentence starter
        if (capitalizedStarters.test(sentence) || lowercaseStarters.test(sentence)) {
          result.push(`${hashes} ${title.trim()}`);
          result.push("");
          result.push(sentence.trim());
          continue;
        }
      }
      
      result.push(line);
      continue;
    }

    // Check for bold titles merged with sentences
    // Pattern: "**Title Here!** It sounds like..." or just "Title Here! It sounds..."
    const boldMergedMatch = line.match(/^(\*\*[^*]+[!?.]?\*\*[!?.]?)\s+([A-Za-z].+)$/);
    if (boldMergedMatch) {
      const [, boldTitle, sentence] = boldMergedMatch;
      if (capitalizedStarters.test(sentence) || lowercaseStarters.test(sentence)) {
        const cleanTitle = boldTitle.replace(/\*\*/g, "").trim();
        result.push(`## ${cleanTitle}`);
        result.push("");
        result.push(sentence.trim());
        continue;
      }
    }

    // Check for plain titles ending with ! or ? merged with sentences
    // Pattern: "Your Next Power Session! It sounds like..."
    const plainMergedMatch = line.match(/^([A-Z][^.!?]{2,50}[!?])\s+([A-Za-z].+)$/);
    if (plainMergedMatch) {
      const [, title, sentence] = plainMergedMatch;
      if (capitalizedStarters.test(sentence) || lowercaseStarters.test(sentence)) {
        result.push(`## ${title.trim()}`);
        result.push("");
        result.push(sentence.trim());
        continue;
      }
    }

    result.push(line);
  }

  return result.join("\n");
};

/**
 * Splits inline bold labels onto separate lines/bullets.
 * Handles: "...text **Total Distance:** 8km **Estimated Duration:** 45 min"
 * becomes separate bullet points for each bold label.
 */
const splitInlineBoldLabels = (text: string): string => {
  const lines = text.split("\n");
  const result: string[] = [];

  for (const line of lines) {
    // Count bold label occurrences like **Label:** in the line
    const boldLabelPattern = /\*\*[A-Z][^*]{1,40}:\*\*/g;
    const matches = [...line.matchAll(boldLabelPattern)];

    // Only split if there are 2+ bold labels crammed into one line
    if (matches.length >= 2) {
      // Check if line starts with a bullet
      const bulletMatch = line.match(/^(\s*[-•]\s*)/);
      const bulletPrefix = bulletMatch ? bulletMatch[1] : "";

      // Split at each bold label boundary (except the first one)
      // Use a regex that captures right before **Label:**
      const parts = line.split(/(?=\*\*[A-Z][^*]{1,40}:\*\*)/g).filter(s => s.trim());

      for (let i = 0; i < parts.length; i++) {
        let part = parts[i].trim();
        // Remove leading pipe separators
        part = part.replace(/^\|\s*/, "").trim();
        if (!part) continue;

        if (i === 0 && bulletPrefix && part.startsWith(bulletPrefix.trim())) {
          result.push(part);
        } else {
          result.push(`${bulletPrefix || "- "}${part}`);
        }
      }
    } else {
      result.push(line);
    }
  }

  return result.join("\n");
};

export const normalizeAiMarkdownForDisplay = (text: string): string => {
  if (!text) return "";

  let out = sanitizeMarkdown(text);

  // Split inline bold labels onto separate lines
  out = splitInlineBoldLabels(out);

  // First: convert inline dashes to proper list items on new lines
  out = convertInlineDashesToList(out);

  // Ensure list bullets are consistent
  out = out.replace(/^\s*\*\s+(?!\*)/gm, "- ");

  // Split titles that are merged with following sentences
  out = splitMergedTitleSentences(out);

  // Convert "bold-only" lines into proper headings, and split accidental "heading + sentence" combos.
  // Example: **Bench Press Breakdown Excellent work today** ->
  // ## Bench Press Breakdown
  // Excellent work today
  const lines = out.split("\n");
  const transformed: string[] = [];

  const separatorCandidates = [": ", " — ", " – ", " - ", " | "];
  const titleSuffixPhrases = [
    "Breakdown",
    "Summary",
    "Analysis",
    "Review",
    "Recap",
    "Overview",
    "Plan",
    "Checklist",
    "Next Steps",
    "Key Takeaways",
    "Takeaways",
    "Action Plan",
  ];

  const sentenceStarterRe = /^(?:That's|That's|This|You're|You're|Great|Excellent|Nice|Solid|Good|Amazing|Keep|Now|Today|Here)\b/i;

  const toHeading = (level: string, headingText: string) => {
    // Keep headings consistent: Arnold is instructed to use ##, but preserve existing level if present.
    const safeLevel = level.startsWith("#") ? level : "##";
    return `${safeLevel} ${headingText}`;
  };

  for (const line of lines) {
    const boldOnly = line.match(/^\s*\*\*(.+?)\*\*\s*$/);
    const headingLine = line.match(/^\s*(#{2,4})\s+(.+?)\s*$/);

    if (!boldOnly && !headingLine) {
      transformed.push(line);
      continue;
    }

    const level = headingLine ? headingLine[1] : "##";
    const innerRaw = (boldOnly ? boldOnly[1] : headingLine![2]).trim();

    if (!innerRaw) continue;

    const words = innerRaw.split(/\s+/).filter(Boolean);
    const withinHeadingBounds = words.length <= 10 && innerRaw.length <= 80;

    let title: string | null = null;
    let rest: string | null = null;
    let splitWasExplicit = false;

    // 1) Prefer explicit separators
    for (const sep of separatorCandidates) {
      const idx = innerRaw.indexOf(sep);
      if (idx > 0 && idx < innerRaw.length - sep.length) {
        title = innerRaw.slice(0, idx).trim();
        rest = innerRaw.slice(idx + sep.length).trim();
        splitWasExplicit = true;
        break;
      }
    }

    // 2) Split after common section-name suffixes (generic, not workout-specific)
    if (!title || !rest) {
      for (const phrase of titleSuffixPhrases) {
        const re = new RegExp(`\\b${phrase.replace(/[-/\\^$*+?.()|[\\]{}]/g, "\\$&")}\\b`, "i");
        const match = innerRaw.match(re);
        if (match && match.index !== undefined) {
          const end = match.index + match[0].length;
          if (end < innerRaw.length - 1) {
            const after = innerRaw.slice(end).trim();
            if (after) {
              title = innerRaw.slice(0, end).trim();
              rest = after;
              splitWasExplicit = true;
              break;
            }
          }
        }
      }
    }

    // 3) Fallback: only split if the remainder looks like a sentence (prevents random truncation)
    if ((!title || !rest) && withinHeadingBounds && words.length >= 6) {
      const titleWordCount = words.length >= 9 ? 4 : 3;
      const candidateTitle = words.slice(0, titleWordCount).join(" ").trim();
      const candidateRest = words.slice(titleWordCount).join(" ").trim();

      if (candidateRest && sentenceStarterRe.test(candidateRest)) {
        title = candidateTitle;
        rest = candidateRest;
      }
    }

    if (title && rest && (splitWasExplicit || sentenceStarterRe.test(rest))) {
      transformed.push(toHeading(level, title));
      transformed.push("");
      transformed.push(rest);
      continue;
    }

    if (withinHeadingBounds) {
      // Convert bold-only "headlines" into real headings.
      // Keep existing headings as headings.
      transformed.push(toHeading(level, innerRaw));
    } else {
      // Too long for a heading — keep as plain paragraph text.
      transformed.push(innerRaw);
    }
  }

  out = transformed.join("\n");

  // Clean up excessive blank lines
  out = out.replace(/\n{3,}/g, "\n\n");

  return out.trim();
};
